import React, { useEffect } from "react";
import {
  Button,
  Menu,
  MenuButton,
  MenuItem,
  MenuList,
  useToast,
} from "@chakra-ui/react";

import { FiChevronDown } from "react-icons/fi";
import { useLocalStorage } from "react-use";
import { useSetCourseLanguageMutation } from "@graphql";

export const useLanguageSwitcher = (slug: string, defaultLanguage?: string) => {
  return useLocalStorage(`language:${slug}`, defaultLanguage ?? "JavaScript");
};

const LanguageSwitcher = ({
  languages,
  activeLanguage,
  setLanguage,
  eventSource,
  courseId,
}: {
  languages: readonly string[];
  activeLanguage?: string;
  setLanguage: (lang: string) => void;
  eventSource: string;
  courseId: string;
}) => {
  const toast = useToast();

  // close toasts when component unmounts
  useEffect(() => () => toast.closeAll(), [toast]);

  const [setCourseLanguage] = useSetCourseLanguageMutation();

  return (
    <Menu>
      <MenuButton
        as={Button}
        size="sm"
        display="flex"
        px={2}
        py={0}
        borderRadius="md"
        borderWidth="1px"
        _focus={{ boxShadow: "outline" }}
        rightIcon={<FiChevronDown />}
      >
        {activeLanguage}
      </MenuButton>
      <MenuList>
        {languages.map((lang) => {
          return (
            <MenuItem
              key={lang}
              onClick={() => {
                setLanguage(lang);
                setCourseLanguage({
                  variables: {
                    courseId,
                    language: lang,
                  },
                });

                window.gtag?.("event", "change_language", {
                  language: lang,
                  source: eventSource,
                });

                if (lang !== "JavaScript" && lang !== activeLanguage) {
                  toast({
                    position: "top",
                    description:
                      "Please note that your language selection will be applied to code blocks and exercises. Videos may not yet reflect your preference.",
                    status: "info",
                    duration: 3500,
                    isClosable: true,
                    containerStyle: {
                      marginTop: 63,
                      maxWidth: "100%",
                    },
                  });
                }
              }}
            >
              {lang}
            </MenuItem>
          );
        })}
      </MenuList>
    </Menu>
  );
};

export default LanguageSwitcher;
